import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';

interface Academy {
  id: string;
  name: string;
}

interface EditProfileHighlightsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    fullName: string;
    username: string;
    beltRank: string;
    academies: Academy[];
    isInstructor: boolean;
  }) => Promise<void>;
  initialData: {
    fullName: string;
    username: string;
    beltRank: string;
    academies: Academy[];
    email: string;
    birthday: string;
    isInstructor: boolean;
  };
}

const BELT_OPTIONS = [
  { value: 'white', label: 'White Belt' },
  { value: 'blue', label: 'Blue Belt' },
  { value: 'purple', label: 'Purple Belt' },
  { value: 'brown', label: 'Brown Belt' },
  { value: 'black', label: 'Black Belt' },
];

const USER_TYPE_OPTIONS = [
  { value: 'practitioner', label: 'Praticante' },
  { value: 'instructor', label: 'Instrutor' },
];

export const EditProfileHighlightsModal: React.FC<EditProfileHighlightsModalProps> = ({ open, onClose, onSave, initialData }) => {
  const [fullName, setFullName] = useState(initialData.fullName);
  const [username, setUsername] = useState(initialData.username);
  const [beltRank, setBeltRank] = useState(initialData.beltRank || 'white');
  const [academies, setAcademies] = useState<Academy[]>(initialData.academies || []);
  const [isInstructor, setIsInstructor] = useState(initialData.isInstructor || false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [email, setEmail] = useState(initialData.email || '');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable' | 'invalid'>('idle');
  const [usernameMessage, setUsernameMessage] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [openBelt, setOpenBelt] = useState(false);
  const [openUserType, setOpenUserType] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    setFullName(initialData.fullName);
    setUsername(initialData.username);
    setBeltRank(initialData.beltRank || 'white');
    setAcademies(initialData.academies || []);
    setIsInstructor(initialData.isInstructor || false);
    setEmail(initialData.email || '');
  }, [initialData]);

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!fullName.trim()) errs.fullName = t('profile.name_required');
    if (!username.trim()) errs.username = t('profile.username_required');
    
    // Only validate username if it was changed
    const usernameUnchanged = username === initialData.username;
    if (!usernameUnchanged) {
      if (!validateUsername(username)) errs.username = t('profile.username_invalid');
      if (usernameStatus === 'unavailable') errs.username = t('profile.username_unavailable');
      if (usernameStatus === 'invalid') errs.username = usernameMessage;
    } else {
      // If username is unchanged, remove any username-related errors
      delete errs.username;
    }
    
    // Academies are optional - no validation required
    return errs;
  };

  const isFormValid = () => {
    const errs = validate();
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    
    setSaving(true);
    try {
      await onSave({ fullName, username: username.toLowerCase(), beltRank, academies, isInstructor });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleAcademyChange = (academy: Academy | null) => {
    if (academy && !academies.find(a => a.id === academy.id)) {
      setAcademies([...academies, academy]);
    }
  };

  const handleRemoveAcademy = (id: string) => {
    setAcademies(academies.filter(a => a.id !== id));
  };

  const validateUsername = (value: string) => {
    return /^[a-z0-9]{5,}$/.test(value);
  };

  const checkUsernameAvailability = async (value: string) => {
    if (!validateUsername(value)) {
      setUsernameStatus('invalid');
      setUsernameMessage('Username must be at least 5 characters long and contain only lowercase letters and numbers.');
      return;
    }
    setUsernameStatus('checking');
    setUsernameMessage('Checking username...');
    // Supabase RPC call
    const { data, error } = await supabase.rpc('check_username_available', { input_username: value });
    if (error) {
      setUsernameStatus('invalid');
      setUsernameMessage('Error checking username.');
      return;
    }
    if (data) {
      setUsernameStatus('available');
      setUsernameMessage('Username available');
    } else {
      setUsernameStatus('unavailable');
      setUsernameMessage('This username is not available, type another.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-2xl bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_profile_highlights')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('auth.email')}</label>
            <Input value={email || initialData.email || ''} disabled readOnly className="text-black border-black bg-gray-100" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.full_name')}</label>
            <Input value={fullName} onChange={e => setFullName(e.target.value)} disabled={saving} className="text-black border-black" />
            {errors.fullName && <div className="text-xs text-red-500 mt-1">{errors.fullName}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.username')}</label>
            <Input
              value={username}
              onChange={e => {
                setUsername(e.target.value);
                setUsernameTouched(true);
                setUsernameStatus('idle');
                setUsernameMessage('');
              }}
              onBlur={async () => {
                setUsernameTouched(true);
                await checkUsernameAvailability(username);
              }}
              disabled={saving}
              className="text-black border-black"
              maxLength={32}
            />
            {usernameTouched && usernameStatus === 'invalid' && (
              <div className="text-xs text-red-500 mt-1">{usernameMessage}</div>
            )}
            {usernameTouched && usernameStatus === 'unavailable' && (
              <div className="text-xs text-red-500 mt-1">{usernameMessage}</div>
            )}
            {usernameTouched && usernameStatus === 'available' && (
              <div className="text-xs text-green-600 mt-1">{usernameMessage}</div>
            )}
            {usernameTouched && usernameStatus === 'checking' && (
              <div className="text-xs text-gray-500 mt-1">{usernameMessage}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.user_type')}</label>
            <div className="w-full border border-black rounded-md">
              <Select 
                value={isInstructor ? 'instructor' : 'practitioner'} 
                onValueChange={(value) => setIsInstructor(value === 'instructor')} 
                disabled={saving} 
                open={openUserType} 
                onOpenChange={setOpenUserType}
              >
                <SelectTrigger
                  className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent relative [&_[data-radix-select-icon]]:hidden"
                  style={{ boxShadow: 'none', outline: 'none', border: 0, background: 'transparent' }}
                >
                  <SelectValue placeholder={t('profile.user_type')} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {openUserType ? (
                      <ChevronUp className="w-5 h-5 text-black" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-black" />
                    )}
                  </span>
                </SelectTrigger>
                <SelectContent className="text-black border-black">
                  {USER_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-black">
                      {t(`profile.${opt.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.belt_rank')}</label>
            <div className="w-full border border-black rounded-md">
              <Select value={beltRank} onValueChange={setBeltRank} disabled={saving} open={openBelt} onOpenChange={setOpenBelt}>
                <SelectTrigger
                  className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent relative [&_[data-radix-select-icon]]:hidden"
                  style={{ boxShadow: 'none', outline: 'none', border: 0, background: 'transparent' }}
                >
                  <SelectValue placeholder={t('profile.belt_rank')} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {openBelt ? (
                      <ChevronUp className="w-5 h-5 text-black" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-black" />
                    )}
                  </span>
                </SelectTrigger>
                <SelectContent className="text-black border-black">
                  {BELT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-black">
                      {t(`profile.${opt.value}_belt`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm mb-1 font-medium text-black">{t('profile.add_your_jiu_jitsu_school')}</label>
            <AcademyAutocomplete
              value={academies[0] ? academies[0] : ''}
              onChange={academy => {
                if (academy && typeof academy === 'object') {
                  setAcademies([academy]);
                } else {
                  setAcademies([]);
                }
              }}
              className="text-black border-black"
            />
            {academies[0] && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span key={academies[0].id} className="bg-gray-200 text-gray-800 rounded-full px-3 py-1 text-xs flex items-center">
                  {academies[0].name}
                  <button
                    type="button"
                    className="ml-2 text-red-500 hover:text-red-700 focus:outline-none"
                    onClick={() => setAcademies([])}
                    disabled={saving}
                  >
                    ×
                  </button>
                </span>
              </div>
            )}
            {errors.academies && <div className="text-xs text-red-500 mt-1">{errors.academies}</div>}
          </div>
        </div>
        <DialogFooter className="mt-6 flex gap-2">
          <Button
            onClick={handleSave}
            disabled={saving || !isFormValid()}
            type="button"
            className="w-full bg-sossiety-red-600 hover:bg-sossiety-red-700 text-white rounded-full py-3 text-base font-semibold transition-colors duration-200 disabled:opacity-50"
            style={{ borderRadius: '9999px' }}
          >
            {saving ? t('profile.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 